import styled from "styled-components";
import { Flex, Text, Image } from "rebass/styled-components";
import { useHistory } from "react-router-dom";
import { FaUsers } from "react-icons/fa";
import { BsArrowRight } from "react-icons/bs";

import { ITeam } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import { GamePills } from "MVVM/Components/Teams/GamePills";
import { formatRelativeTime } from "MVVM/Utilities/Time";
import { colors } from "theme/colors";

interface IDiscoverCardProps {
  team: ITeam;
}

const TIER_TEXT_COLORS: Record<string, string> = {
  FREE: colors.tierTextFree,
  ESSENTIALS: colors.tierTextEssentials,
  PRO: colors.tierTextPro,
  PRO_PLUS: colors.tierTextPro,
  CREATOR_PLAT: colors.tierTextPlat,
  INTERNAL: colors.tierTextInternal,
};

export const DiscoverCard = ({ team }: IDiscoverCardProps) => {
  const history = useHistory();
  const fallbackCover = `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/profile/banner/allstar.jpg`;
  const coverUrl = team.coverUrl ?? fallbackCover;

  const ownerTier = (team.owner?.userGroupType ??
    team.owner?.userGroup?.type ??
    "FREE") as string;
  const tierColor = TIER_TEXT_COLORS[ownerTier] || "";

  return (
    <Card
      onClick={() => {
        analytics.track("Discover Team Card Clicked", {
          teamSlug: team.slug,
          teamName: team.name,
          memberCount: team.memberCount ?? team.members?.length ?? 0,
        });
        history.push(`/team/${team.slug}`);
      }}
    >
      <Banner bannerUrl={coverUrl}>
        <BannerScrim />
      </Banner>
      <Body>
        <TopRow>
          <Avatar src={team.avatarUrl} alt={team.name} />
          <TitleBlock>
            <TeamName>{team.name}</TeamName>
            <MetaRow>
              {team.tag && <Tag>{team.tag}</Tag>}
              <MemberCount>
                <FaUsers size={13} />
                {(
                  team.memberCount ??
                  team.members?.length ??
                  0
                ).toLocaleString()}{" "}
                {(team.memberCount ?? team.members?.length ?? 0) === 1
                  ? "member"
                  : "members"}
              </MemberCount>
            </MetaRow>
          </TitleBlock>
        </TopRow>

        {team.owner && (
          <CreatorRow>
            <CreatorAvatar src={team.owner.avatarUrl ?? undefined} />
            <CreatorLabel>Created by</CreatorLabel>
            <CreatorName
              $color={tierColor}
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/u/${team.owner!.username}`);
              }}
            >
              @{team.owner.username}
            </CreatorName>
            {team.owner.followerCount != null &&
              team.owner.followerCount > 0 && (
                <FollowerCount>
                  · {formatCount(team.owner.followerCount)} followers
                </FollowerCount>
              )}
          </CreatorRow>
        )}

        <Bio>{team.bio || "This team hasn't added a bio yet."}</Bio>

        <GamePillsRow>
          <GamePills playedGames={team.playedGames} />
        </GamePillsRow>
      </Body>

      <Footer>
        <FooterMeta>
          {team.createdAt
            ? `Founded ${formatRelativeTime(team.createdAt)}`
            : `${team.memberCount ?? team.members?.length ?? 0} Members`}
        </FooterMeta>
        <OpenButton
          onClick={(e) => {
            e.stopPropagation();
            history.push(`/team/${team.slug}`);
          }}
        >
          Open Team <BsArrowRight size={14} />
        </OpenButton>
      </Footer>
    </Card>
  );
};

const formatCount = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

const Card = styled(Flex)`
  background: ${({ theme }) => theme.colors.navy300};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 12px;
  cursor: pointer;
  flex-direction: column;
  overflow: hidden;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.navy};
  }
`;

const Banner = styled(Flex)<{ bannerUrl: string }>`
  background: url(${({ bannerUrl }) => bannerUrl}) center / cover no-repeat;
  height: 120px;
  position: relative;
  width: 100%;
`;

const BannerScrim = styled.div`
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.2) 0%,
    ${({ theme }) => theme.colors.navy300} 100%
  );
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const Body = styled(Flex)`
  flex: 1;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
`;

const TopRow = styled(Flex)`
  align-items: center;
  gap: 14px;
  margin-top: -44px;
  position: relative;
  z-index: 1;
`;

const Avatar = styled(Image)`
  background: ${({ theme }) => theme.colors.navy};
  border: 3px solid ${({ theme }) => theme.colors.navy300};
  border-radius: 12px;
  flex-shrink: 0;
  height: 60px;
  object-fit: cover;
  width: 60px;
`;

const TitleBlock = styled(Flex)`
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const TeamName = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MetaRow = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  flex-wrap: wrap;
  font-size: 14px;
  gap: 10px;
`;

const Tag = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const MemberCount = styled(Flex)`
  align-items: center;
  gap: 5px;
`;

const CreatorRow = styled(Flex)`
  align-items: center;
  gap: 6px;
`;

const CreatorAvatar = styled(Image)`
  border-radius: 50%;
  flex-shrink: 0;
  height: 24px;
  object-fit: cover;
  width: 24px;
`;

const CreatorLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
`;

const CreatorName = styled.span<{ $color: string }>`
  color: ${({ $color, theme }) => $color || theme.colors.ice};
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;

  &:hover {
    text-decoration: underline;
  }
`;

const FollowerCount = styled(Text)`
  color: ${({ theme }) => theme.colors.shade300};
  font-size: 13px;
`;

const Bio = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  display: -webkit-box;
  font-size: 15px;
  line-height: 22px;
  max-height: 44px;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const GamePillsRow = styled(Flex)`
  margin-top: auto;
`;

const Footer = styled(Flex)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.navy50};
  justify-content: space-between;
  padding: 14px 20px;
`;

const FooterMeta = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 500;
`;

const OpenButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  display: flex;
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 0.3px;
  padding: 0;
  text-transform: uppercase;

  &:hover {
    opacity: 0.8;
  }
`;
